import type { VariantProps } from "class-variance-authority";
import type React from "react";
import type { MouseEventHandler, ReactElement } from "react";

import { cva } from "class-variance-authority";
import { forwardRef, useRef } from "react";
import { cn } from "../utils/classnames";
import { FormError } from "./FormError";

export interface CheckboxProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, error, ...props }, ref) => {
    return (
      <div className="relative inline-block">
        <input
          className={cn("absolute z-10 h-full w-full cursor-pointer opacity-0")}
          ref={ref}
          type="checkbox"
          {...props}
        />
        <div
          aria-hidden
          className={cn(
            "pointer-events-none flex h-4 w-4 items-center justify-center rounded-[2px] border border-new-beginnings-base bg-white",
            props.checked ? "bg-new-beginnings-base" : "",
            "group-hover:border-new-beginnings-base",
            className,
          )}
        >
          {props.checked && (
            <svg
              className="h-3 w-3 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M5 13l4 4L19 7"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
          )}
        </div>
        {error && (
          <FormError.p className="mt-1 text-error">{error}</FormError.p>
        )}
      </div>
    );
  },
);

Checkbox.displayName = "Checkbox";

const checkboxButtonVariants = cva(
  "group flex w-full cursor-pointer items-center justify-between gap-6 rounded-sm border border-2 border-transparent bg-white p-4 text-new-beginnings-base hover:border-new-beginnings-base hover:bg-new-beginnings-tints-800 hover:text-new-beginnings-base hover:shadow-primary-button hover:outline-offset-2 focus:outline focus:outline-2 focus:outline-new-beginnings-base disabled:cursor-not-allowed disabled:opacity-50 md:w-[424px]",
  {
    defaultVariants: {
      buttonSize: "default",
    },
    variants: {
      buttonSize: {
        default: "h-[72px]",
        large: "h-[104px]",
        short: "h-[48px]",
      },
    },
  },
);

export interface CheckboxButtonProps
  extends Omit<CheckboxProps, "className">,
    VariantProps<typeof checkboxButtonVariants> {
  label?: string | ReactElement;
  labelClassName?: string;
  containerClassName?: string;
}

export const CheckboxButton: React.FC<CheckboxButtonProps> = ({
  labelClassName,
  containerClassName,
  buttonSize,
  children,
  ...checkboxProps
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleContainerClick: MouseEventHandler<HTMLDivElement> = (e) => {
    if (e.target !== inputRef.current) {
      e.preventDefault();
      if (inputRef.current && !inputRef.current.disabled) {
        const newChecked = !inputRef.current.checked;
        inputRef.current.checked = newChecked;

        if (checkboxProps.onChange) {
          const changeEvent = {
            target: {
              checked: newChecked,
              id: checkboxProps.id,
              name: checkboxProps.name,
            },
          } as React.ChangeEvent<HTMLInputElement>;

          checkboxProps.onChange(changeEvent);
        }

        inputRef.current.focus();
      }
    }
  };

  return (
    <div
      aria-checked={checkboxProps.checked}
      className={cn(
        checkboxButtonVariants({ buttonSize }),
        containerClassName,
        {
          "border-2 border-new-beginnings-base": checkboxProps.checked,
          "cursor-not-allowed opacity-50": checkboxProps.disabled,
        },
      )}
      onClick={handleContainerClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          handleContainerClick(
            e as unknown as React.MouseEvent<HTMLDivElement>,
          );
        }
      }}
      role="checkbox"
      tabIndex={0}
    >
      {children && (
        <div>
          <label
            className={cn(
              "flex cursor-pointer items-center gap-6 font-bold text-new-beginnings-base",
              checkboxProps.error && "text-error",
              labelClassName,
            )}
            htmlFor={checkboxProps.id}
          >
            {children}
          </label>
        </div>
      )}
      <div className="flex h-5 items-center">
        <Checkbox
          ref={inputRef}
          {...checkboxProps}
          aria-label={
            checkboxProps["aria-label"] ||
            `Select ${checkboxProps.name || "option"}`
          }
          tabIndex={-1}
        />
      </div>
    </div>
  );
};
